import React from "react";
import {
  FeatherTrendingUp,
  FeatherTrendingDown,
  FeatherPieChart,
} from "@subframe/core";

export type PlMetricIcon = "trendingUp" | "trendingDown" | "pieChart";

export type PlMetricChangeVariant = "success" | "error" | "neutral";

export interface PlMetricCardProps {
  label: string;
  /** Second line under the title, smaller type (e.g. YTD range from API). */
  labelRangeSubtext?: string;
  value: string;
  icon: PlMetricIcon;
  changeText?: string;
  changeVariant?: PlMetricChangeVariant;
  subtext?: string;
  singleLineFooterText?: string;
}

const IconMap: Record<PlMetricIcon, React.ComponentType<{ className?: string }>> =
  {
    trendingUp: FeatherTrendingUp,
    trendingDown: FeatherTrendingDown,
    pieChart: FeatherPieChart,
  };

export function PlMetricCard({
  label,
  labelRangeSubtext,
  value,
  icon,
  changeText,
  changeVariant = "neutral",
  subtext,
  singleLineFooterText,
}: PlMetricCardProps) {
  const Icon = IconMap[icon];

  const changeColorClass =
    changeVariant === "success"
      ? "text-success-600"
      : changeVariant === "error"
        ? "text-error-600"
        : "text-neutral-600";

  const isNoPriorYearData = Boolean(
    changeText?.toLowerCase().includes("no previous year data") ||
      changeText?.toLowerCase().includes("no prior year")
  );
  const showSubtext = subtext && !isNoPriorYearData;

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start overflow-hidden rounded-xl border border-solid border-neutral-200">
      <div className="flex w-full flex-col items-start gap-4 bg-white px-5 py-5">
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 pr-1">
            <span className="text-body font-body text-neutral-700">{label}</span>
            {labelRangeSubtext?.trim() ? (
              <span className="text-caption font-caption leading-snug text-neutral-500">
                {labelRangeSubtext.trim()}
              </span>
            ) : null}
          </div>
          <div className="flex h-8 w-8 flex-none shrink-0 flex-col items-center justify-center rounded-full bg-neutral-50">
            <Icon className="text-body font-body text-neutral-500" />
          </div>
        </div>
        <span className="text-heading-1 font-heading-1 text-neutral-900">
          {value}
        </span>
      </div>

      {singleLineFooterText ? (
        <div className="flex w-full items-center gap-1 border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3">
          <span className="text-caption-bold font-caption-bold text-neutral-600">
            {singleLineFooterText}
          </span>
        </div>
      ) : (
        <div className="flex w-full items-center gap-1 border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3">
          {changeText && (
            <span
              className={`text-caption-bold font-caption-bold ${changeColorClass}`}
            >
              {changeText}
            </span>
          )}
          {showSubtext && (
            <span className="text-caption font-caption text-neutral-500">
              {subtext}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

